// Status bar (CONTRACT "Status bar", live.html:173-179): 24 px on the floor
// step. Current destination, record count from coverage.matchingRecords,
// evidence freshness with the CEST or CET label, the state, the account
// label, and the key hint slot at the end.
//
// The state cell is always drawn, Ready included, as the state pill primitive
// (`.state[data-state]` in live.html; StatePill here). Hiding it on Ready left
// the footer with no state at all, which is the one thing feature contract 2.5
// asks the footer for: "stale or disconnected state without claiming live
// data". It is a button because the reason belongs somewhere a pointer user
// can reach without hovering, and what it opens depends on the state: a dead
// provider opens Provider Health, an ended session re-runs the access check.
//
// It also carries the way back to a collapsed tape, so collapsing can take the
// tape row to 0 instead of leaving a permanent 24 px strip above the
// workspace.
import { CaretDown } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { ARCHIVE_PAGE_LIMIT } from '../api/config';
import { useArchiveCalls } from '../api/hooks';
import { queryKeys } from '../api/queryKeys';
import type { ArchiveCoverage, AuthIdentity } from '../api/types';
import { useView } from '../router/useView';
import type { HonestStateVariant } from '../states/HonestState';
import { evidenceAge, evidenceIsStale, stateOfError, stateOfSourceHealth } from '../states/stateOf';
import { formatZurich, formatZurichDayTime } from '../time/zurich';
import { Button } from '../ui/Button';
import { KeyCap } from '../ui/KeyCap';
import { StatePill } from '../ui/StatePill';
import { canAccessView, labelOf, viewAccessReason } from './destinations';
import { useShell } from './ShellContext';
import { MENU_MAX_WIDTH, SHEET_MAX_WIDTH, useNarrow } from './useNarrow';
import { useOnline } from './useOnline';
import { STATUS_KEY_CAPS } from './useKeys';
import { useNow } from './useNow';

const TAPE_FILTERS = { limit: ARCHIVE_PAGE_LIMIT.tape } as const;

export interface StatusState {
  readonly variant: HonestStateVariant;
  readonly reason: string;
}

// One state for the whole footer, in the order a reader cares about: an ended
// session first, then a browser with no connection, then whatever the last
// answer actually was, then retained-but-stale rows, then evidence that
// stopped arriving, then source health, then Loading.
//
// Three of those steps exist because of a measured wrong answer.
//
// The session step: with every /api/beta call answering 401, the footer read
// "Stale. The server answered 401 request_failed. Sign in to continue." for
// 35 s while a hundred dead rows stayed on screen and the only control opened
// Provider Health. The session is the reason, so it is the state.
//
// The offline step: sixty seconds offline, the footer still blamed the
// Telegram import's heartbeat. Query pauses rather than fails while the
// browser is offline, so no error ever arrives to correct it.
//
// The error step used to hardcode "Provider unavailable". A 429 therefore
// printed the loss colour in the footer while the workspace beside it printed
// the same 429 as "Rate limited" in warn. The observed code decides.
//
// The freshness step is not decoration either. Without it the footer printed
// "Evidence 14 Aug 06:19 CEST" and "Ready" side by side, five days apart.
export function statusState(input: {
  readonly error: unknown;
  readonly stale: boolean;
  readonly isLoading: boolean;
  readonly health: ArchiveCoverage['sourceHealth'] | null;
  readonly latestEvidenceObservedAt?: string | null;
  readonly now?: number;
  // False only when the browser reports no connection at all.
  readonly online?: boolean;
  // Whether this browser is still holding rows it loaded earlier.
  readonly retained?: boolean;
}): StatusState {
  const observed = input.error ? stateOfError(input.error) : null;
  if (
    observed?.variant === 'Authentication required' ||
    observed?.variant === 'Permission denied'
  ) {
    return {
      variant: observed.variant,
      reason: `${observed.reason} Anything still on screen is what this browser already held.`,
    };
  }
  if (input.online === false) {
    const since = formatZurichDayTime(input.latestEvidenceObservedAt ?? null);
    if (input.retained) {
      return {
        variant: 'Stale',
        reason: `This browser is offline, so nothing has refreshed${since ? ` since the evidence of ${since}` : ''}. The rows shown are the ones it already held.`,
      };
    }
    return {
      variant: 'Provider unavailable',
      reason:
        'This browser is offline, so the workspace service cannot be reached. Nothing is loaded.',
    };
  }
  if (observed && !input.stale) {
    return { variant: observed.variant, reason: observed.reason };
  }
  if (input.stale) {
    return {
      variant: 'Stale',
      reason: observed
        ? observed.reason
        : 'The last refresh did not answer; the rows shown are the ones this browser already held.',
    };
  }
  const now = input.now ?? Date.now();
  const latest = input.latestEvidenceObservedAt ?? null;
  if (latest !== null && evidenceIsStale(latest, now)) {
    return {
      variant: 'Stale',
      reason: `The newest evidence in this workspace is ${evidenceAge(latest, now)} old (${formatZurichDayTime(latest) ?? 'time unavailable'}). The connected route is a read-only import with no heartbeat.`,
    };
  }
  if (input.health) return stateOfSourceHealth(input.health.state, input.health.connectors.length);
  if (input.isLoading)
    return { variant: 'Loading', reason: 'Reading the evidence archive for this workspace.' };
  return {
    variant: 'Partial',
    reason: 'The archive answered without coverage, so freshness and record count are unknown.',
  };
}

// States the reader cannot act on from Provider Health get their own way out.
// An ended session re-runs access; an owner-only denial returns to the desk.
export function statusAction(variant: HonestStateVariant): {
  readonly label: string;
  readonly kind: 'health' | 'access' | 'desk';
} {
  if (variant === 'Authentication required') {
    return { label: 'Check the session', kind: 'access' };
  }
  if (variant === 'Permission denied') {
    return { label: 'Open Research desk', kind: 'desk' };
  }
  return { label: 'Open Provider Health', kind: 'health' };
}

export function StatusBar({
  identity,
  accessRole,
}: {
  readonly identity: AuthIdentity | null;
  readonly accessRole?: AuthIdentity['role'] | null;
}) {
  const { view, navigate } = useView();
  const { destinationStatus, tapeOpen, toggleTape, viewKeys } = useShell();
  const queryClient = useQueryClient();
  const calls = useArchiveCalls(TAPE_FILTERS);
  const online = useOnline();
  // Below 1024 the rail and the list are already a menu button, and the full
  // cap list (11 caps, about 260 px) pushed the freshness cell to a measured
  // clientWidth of 0 at 768 while the account name kept 10 px of the 30 it
  // needed. The caps are the redundant half: the "?" sheet prints all of them
  // and the "?" cap is the way to it, so at this width the slot keeps that one.
  const narrow = useNarrow(MENU_MAX_WIDTH);
  // At 390 even the bare instant is 105 px against the 88 px the strip can
  // spare, so there the cell prints the age instead: "6 days old" is the fact
  // a reader is actually reading the timestamp for, and the instant stays in
  // the title.
  const tight = useNarrow(SHEET_MAX_WIDTH);
  // The clock the ages read from: a ticking value rather than Date.now during
  // render, which is what keeps the age honest as the minutes pass.
  const now = useNow();

  const coverage = calls.coverage;
  const role = accessRole ?? identity?.role ?? null;
  const activeDestinationStatus = canAccessView(view, role)
    ? destinationStatus
    : {
        records: 'records unavailable',
        observedAt: null,
        freshnessLabel: labelOf(view),
        emptyFreshness: 'Owner access required',
        state: { variant: 'Permission denied' as const, reason: viewAccessReason(view) },
        retained: false,
      };
  const observed =
    activeDestinationStatus?.observedAt ?? coverage?.latestEvidenceObservedAt ?? null;
  const records =
    activeDestinationStatus?.records ??
    (coverage
      ? `${coverage.matchingRecords.toLocaleString('en-GB')} records`
      : calls.isLoading
        ? 'counting records'
        : 'records unavailable');
  const observedAt = observed ? formatZurich(observed, { date: 'dayMonth', seconds: false }) : null;
  const freshnessLabel = activeDestinationStatus?.freshnessLabel ?? 'Evidence';
  // Under 1024 the cell drops the word and keeps the instant, which is the
  // half a reader cannot reconstruct. The sentence stays in the title.
  const freshness = observedAt
    ? tight
      ? `${evidenceAge(observed, now)} old`
      : narrow
        ? observedAt
        : `${freshnessLabel} ${observedAt}`
    : activeDestinationStatus
      ? activeDestinationStatus.emptyFreshness
      : coverage
        ? 'No evidence observed'
        : 'Freshness unknown';
  const freshnessTitle = observedAt
    ? `Newest ${freshnessLabel.toLowerCase()} observed ${observedAt}`
    : freshness;
  const archiveState = statusState({
    error: calls.error,
    stale: Boolean(calls.stale),
    isLoading: calls.isLoading,
    health: coverage ? coverage.sourceHealth : null,
    latestEvidenceObservedAt: observed,
    online,
    retained: calls.rows.length > 0,
  });
  const state = activeDestinationStatus
    ? online
      ? activeDestinationStatus.state
      : statusState({
          error: null,
          stale: false,
          isLoading: false,
          health: null,
          latestEvidenceObservedAt: activeDestinationStatus.observedAt,
          online,
          retained: activeDestinationStatus.retained,
        })
    : archiveState;
  const action = statusAction(state.variant);
  const onAct = () => {
    if (action.kind === 'access') {
      // reset, not invalidate. Query keeps the last good answer when a refetch
      // fails, so invalidating left the cached identity in place and the
      // control looked inert: measured, the door was still not reached 15 s
      // after the click. Resetting drops what this browser assumed and asks
      // again from nothing, which is what the label promises. The shell
      // remounts through the boot access check on the way, and lands on the
      // door with the session-ended notice when the workspace says 401.
      void queryClient.resetQueries({ queryKey: queryKeys.authMe() });
      return;
    }
    if (action.kind === 'desk') {
      navigate('desk');
      return;
    }
    navigate('health');
  };

  // Shell caps first, then whatever the destination registered, without
  // repeating a cap both of them print.
  const caps = narrow
    ? ['?']
    : [...STATUS_KEY_CAPS, ...viewKeys.caps.filter((cap) => !STATUS_KEY_CAPS.includes(cap))];

  return (
    <footer className="status" role="contentinfo">
      <span className="status-where">
        <b>{labelOf(view)}</b>
      </span>
      <span className="status-sep" />
      <span className="status-records num">{records}</span>
      <span className="status-sep" />
      <span className="status-fresh" title={freshnessTitle}>
        {freshness}
      </span>
      <span className="status-sep" />
      <button
        type="button"
        className="status-state"
        aria-label={`${state.variant}. ${state.reason} ${action.label}.`}
        title={state.reason}
        onClick={onAct}
      >
        <StatePill
          variant={state.variant}
          reason={state.reason}
          nextAction={{ label: action.label, onSelect: onAct }}
        />
      </button>
      {identity ? (
        <>
          <span className="status-sep hide-sm" />
          <span className="status-who hide-sm">{identity.user.displayName}</span>
        </>
      ) : null}
      <span className="status-end">
        {tapeOpen ? null : (
          <Button
            className="status-tape"
            variant="tertiary"
            size="sm"
            keyCap="t"
            icon={<CaretDown />}
            aria-label="Show the latest observed tape, key t"
            onClick={toggleTape}
          >
            Latest observed
          </Button>
        )}
        <span className="status-keys hide-sm">
          Keys{' '}
          {caps.map((cap) => (
            <KeyCap key={cap}>{cap}</KeyCap>
          ))}
        </span>
      </span>
    </footer>
  );
}
